import {
  Building,
  DollarSign,
  MapPin,
  Clock,
  Stethoscope,
  AlertCircle,
  ClipboardList,
  ShieldCheck,
  Star,
  FileText,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RenderHtml } from "@/components/RenderHtml";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { getSalaryRange } from "./utils/get-salary-range";
import type { RouterOutputs } from "@/trpc";

type PublicJob = NonNullable<RouterOutputs["jobPost"]["getPublic"]>;

interface PublicJobDetailProps {
  job: PublicJob;
}

// Strips anything that requires an authenticated session (decision buttons,
// share dialog, hiring process view, `EligibilityBadge`, `ExternalJobBadge`)
// since this view is rendered for anonymous visitors.
export function PublicJobDetail({ job }: PublicJobDetailProps) {
  const salaryRange = getSalaryRange(job);
  const location = job.city
    ? job.stateCode
      ? `${job.city}, ${job.stateCode}`
      : job.city
    : job.stateCode || "";
  const facilityName = job?.facility?.name || job.facilityName || "";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 pb-0">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <CardTitle className="text-2xl md:text-3xl font-semibold text-gray-900 leading-tight grow">
              {job.title || "Job Title Not Available"}
            </CardTitle>
          </div>
        </div>

        <div className="px-6 pt-4">
          <div className="flex flex-wrap gap-2">
            {salaryRange && (
              <Badge
                variant="outline"
                className="bg-blue-50 border-blue-200 text-blue-700 rounded-full px-3 py-1 text-sm font-medium"
              >
                <DollarSign className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                {salaryRange}
              </Badge>
            )}
            {(job.durationIds ?? []).map((durationId) => (
              <Badge
                key={durationId}
                variant="outline"
                className="bg-green-50 border-green-200 text-green-700 rounded-full px-3 py-1 text-sm font-medium"
              >
                {getJobDurationLabel(durationId)}
              </Badge>
            ))}
            {job.facilityType && (
              <Badge
                variant="outline"
                className="rounded-full px-3 py-1 text-sm font-medium bg-gray-50 border-gray-200 text-gray-600"
              >
                {job.facilityType.name}
              </Badge>
            )}
            {job.externalUrl && (
              <Badge
                variant="outline"
                className="bg-amber-50 border border-amber-200 text-amber-700 px-2.5 py-1 rounded-md text-sm font-medium"
              >
                <span>External</span>
                <Info
                  className="h-3.5 w-3.5 ml-1.5"
                  aria-label="External job info"
                />
              </Badge>
            )}
          </div>
        </div>

        <div className="px-6 py-5 space-y-3">
          {job.healthcareProfessionalTypes &&
            job.healthcareProfessionalTypes.length > 0 && (
              <div className="flex items-start gap-2.5">
                <Stethoscope className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
                <div className="text-sm">
                  <span className="text-gray-500">
                    {job.healthcareProfessionalTypes
                      .map((type) => type.name)
                      .join(", ")}
                    :
                  </span>{" "}
                  <span className="font-medium text-gray-800">
                    {job.specialties && job.specialties.length > 0 ? (
                      job.specialties.map((s) => s.name).join(", ")
                    ) : (
                      <span className="text-gray-400 italic">
                        Not specified
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

          {facilityName && (
            <div className="flex items-center gap-2.5 text-sm">
              <Building className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="text-gray-700">
                {facilityName}{" "}
                {job?.facilityType ? `(${job.facilityType.name})` : ""}
              </span>
            </div>
          )}

          {location && (
            <div className="flex items-center gap-2.5 text-sm">
              <MapPin className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="text-gray-700">{location}</span>
            </div>
          )}
        </div>
      </div>

      {job.scheduleDescription && (
        <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" aria-hidden="true" />
              Work Schedule Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6 text-sm text-gray-600 leading-relaxed">
            <RenderHtml html={job.scheduleDescription} />
          </CardContent>
        </Card>
      )}

      {job.responsibilities && job.responsibilities.length > 0 && (
        <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ClipboardList
                className="h-4 w-4 text-blue-500"
                aria-hidden="true"
              />
              Responsibilities
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6">
            <ul className="space-y-2.5">
              {job.responsibilities.map((item: string, index: number) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm text-gray-600"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {job.requiredQualifications && job.requiredQualifications.length > 0 && (
        <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck
                className="h-4 w-4 text-blue-500"
                aria-hidden="true"
              />
              Required Qualifications
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 px-6 pb-6">
            <ul className="space-y-2.5">
              {job.requiredQualifications.map((item: string, index: number) => (
                <li
                  key={index}
                  className="flex items-start gap-2.5 text-sm text-gray-600"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {job.preferredQualifications &&
        job.preferredQualifications.length > 0 && (
          <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Star className="h-4 w-4 text-blue-500" aria-hidden="true" />
                Preferred Qualifications
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-6 pb-6">
              <ul className="space-y-2.5">
                {job.preferredQualifications.map(
                  (item: string, index: number) => (
                    <li
                      key={index}
                      className="flex items-start gap-2.5 text-sm text-gray-600"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-300 shrink-0" />
                      {item}
                    </li>
                  ),
                )}
              </ul>
            </CardContent>
          </Card>
        )}

      <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-500" aria-hidden="true" />
            Job Description
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-6 pb-6 text-sm text-gray-600 prose prose-sm max-w-none">
          {job.description ? (
            <RenderHtml html={job.description} />
          ) : (
            <p className="text-gray-400 italic flex items-center gap-2">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              Job description not provided.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
